import { useEffect, useState, useRef } from 'react';
import { createSearchParams, useNavigate } from 'react-router';
import { VegaChip, VegaFlex, VegaFont, VegaPageNotification } from '@globalpayments/vega-react';
import { failure, unauthorized } from '../../types/api';
import { Errors } from '../../types/errors';

import SessionCards from './components/SessionCards';
import SearchBar from '../../components/searchBar';

import apiFunctions from '../../api';
import PageSkeleton from '../../components/pageSkeleton';
import { Card } from './components/CardProps';

const UserLookup = () => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasTyped, setHasTyped] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [cards, setCards] = useState<Card[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    let subscribed = true;

    const backSearch = sessionStorage.getItem('searchVal');
    if (backSearch) {
      setHasTyped(true);
      setSearchValue(backSearch);
      sessionStorage.removeItem('searchVal');
    }

    const searchValues = async (searchValue: string) => {
      if (searchValue === '' || searchValue === undefined || searchValue === null) {
        clearCards();
        setLoading(false);
      } else {
        await apiFunctions.apiPost('Kirby/DeprecatedFeature/GetSessions', { SearchText: searchValue }).then((data) => {
          if (data === failure) {
            navigate({
              pathname: '/error',
              search: `?${createSearchParams({ errType: Errors.CallFailed })}`,
            });
          } else if (data === unauthorized) {
            navigate({
              pathname: '/error',
              search: `?${createSearchParams({ errType: Errors.Unauthorized })}`,
            });
          }

          if (typeof data === 'string') {
            clearCards();
          } else {
            setCards(data);
          }
          setHasSearched(true);
          setLoading(false);
        });
      }
    };

    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
    }
    if (hasTyped) {
      timerRef.current = setTimeout(async () => {
        if (subscribed) {
          setHasTyped(false);
          setLoading(true);
          clearCards();
          searchValues(searchValue);
        }
      }, 400);
    }

    return () => {
      subscribed = false;
    };
  }, [hasTyped, navigate, searchValue]);

  const clearCards = () => {
    setCards([]);
  };

  const onCardClick = (appUid: string) => {
    sessionStorage.setItem('searchVal', searchValue);

    navigate({
      pathname: '/sessioninfo',
      search: `?${createSearchParams({ id: appUid })}`,
    });
  };

  return (
    <VegaFlex direction="col" gap="size-32">
      <SearchBar
        searchHeader="Search applications"
        searchLabel="Email, Salesforce ID, application UID, business, DBA, legal, or location name"
        searchTooltipText="Enter a complete email, Salesforce ID, application UID, DBA or legal name, or location name."
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        clearCards={clearCards}
        setHasTyped={setHasTyped}
      />

      {loading ? (
        <PageSkeleton label="Loading application results" variant="results" />
      ) : cards.length > 0 ? (
        <VegaFlex direction="col" gap="size-16">
          <VegaFlex alignItems="center" flexWrap="wrap" gap="size-8" justifyContent="space-between">
            <VegaFont as="h2" variant="font-h5">
              Results for “{searchValue}”
            </VegaFont>
            <VegaChip
              chipType="label"
              size="small"
              text={`${cards.length} ${cards.length === 1 ? 'result' : 'results'}`}
              variant="outlined"
            />
          </VegaFlex>
          <SessionCards cards={cards} onCardClick={onCardClick} />
        </VegaFlex>
      ) : hasSearched && searchValue ? (
        <VegaPageNotification
          duration={0}
          message={`No applications matched “${searchValue}”. Try another identifier or name.`}
          notificationTitle="No applications found"
          type="info"
        />
      ) : null}
    </VegaFlex>
  );
};

export default UserLookup;
